// src/pages/Booking.jsx â€” FINAL FULL (Car Slots + Motorcycle Slots + Desk Spaces)
// âœ… Keeps all existing booking availability logic intact
// âœ… Commute + Estimated Distance are saved as per-user preference and AUTO-FILL on every modal
// âœ… When user picks commute+distance in Parking, then booking Desk will auto-fill with the same values
// âœ… Prevents user being stuck by "You've already filled this in before."
//    - If saved preference exists and user selects different values, we TRY update preference first
//    - If backend doesn't support / rejects update, fallback to saved preference so booking still proceeds
// âœ… Client-side conflict rule matches backend: Parking <-> Motorcycle mutual exclusivity by overlap
// âœ… Does NOT change resource fetching / map / availability behavior
// âœ… Booking create sends `estimated_distance`; if backend rejects unknown field, retries WITHOUT it (never breaks)

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Car, Monitor, RefreshCcw, Bike } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import Card from '../components/Card.jsx';
import Button from '../components/Button.jsx';
import Input from '../components/Input.jsx';
import Modal from '../components/Modal.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import DeskFloorMap, { DeskFloorPicker } from '../components/DeskFloorMap.jsx';
import { DESK_MAPS, DESK_FLOOR_ORDER } from '../floormaps/desks.js';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';

/* ===================== Util waktu (LOKAL, bukan UTC) ===================== */
const pad = (n) => String(n).padStart(2, '0');
const toSqlLocal = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  const Y = x.getFullYear();
  const M = pad(x.getMonth() + 1);
  const D = pad(x.getDate());
  const h = pad(x.getHours());
  const m = pad(x.getMinutes());
  const s = pad(x.getSeconds());
  return `${Y}-${M}-${D} ${h}:${m}:${s}`;
};
const toInputValue = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  const Y = x.getFullYear();
  const M = pad(x.getMonth() + 1);
  const D = pad(x.getDate());
  const h = pad(x.getHours());
  const m = pad(x.getMinutes());
  return `${Y}-${M}-${D}T${h}:${m}`;
};
const toDateInputValue = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  const Y = x.getFullYear();
  const M = pad(x.getMonth() + 1);
  const D = pad(x.getDate());
  return `${Y}-${M}-${D}`;
};
const fromInputLocal = (str) => {
  if (!str) return new Date('invalid');
  const [day, time] = str.split('T');
  const [Y, M, D] = day.split('-').map(Number);
  const [h, m] = time.split(':').map(Number);
  return new Date(Y, M - 1, D, h, m || 0, 0, 0);
};
const fromDateInputLocal = (str) => {
  if (!str) return new Date('invalid');
  const [Y, M, D] = str.split('-').map(Number);
  if (!Y || !M || !D) return new Date('invalid');
  return new Date(Y, M - 1, D, 0, 0, 0, 0);
};
const ceilMinute = (d = new Date()) => {
  const x = new Date(d);
  x.setSeconds(0, 0);
  return x;
};
const addHours = (d, h) => {
  const x = new Date(d);
  x.setHours(x.getHours() + h);
  return x;
};
const addDays = (d, days) => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
};
// [aStart,aEnd) vs [bStart,bEnd)
const overlaps = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && aEnd > bStart;

/* ===================== Helpers booking ===================== */
const parseUTC = (s) => {
  if (!s) return new Date('invalid');
  if (s.endsWith('Z') || s.includes('T')) return new Date(s);
  return new Date(s.replace(' ', 'T') + 'Z');
};
const normalizeTimes = (b) => {
  const s = b.start_utc || b.start_iso || b.startZ || b.start_time;
  const e = b.end_utc || b.end_iso || b.endZ || b.end_time;
  return { start: parseUTC(s), end: parseUTC(e) };
};
const isCanceled = (b) => {
  const s = (b.status || '').toLowerCase();
  return s === 'canceled' || s === 'cancelled';
};
const isActiveBooking = (b, now = new Date()) => {
  if (isCanceled(b)) return false;
  const { start, end } = normalizeTimes(b);
  if (isNaN(start) || isNaN(end)) return false;
  return end > now;
};

const fmtBookingTs = (s) => {
  if (!s) return '';
  const base = String(s).replace('T', ' ').replace('Z', '');
  return base.length >= 16 ? base.slice(0, 16) : base;
};
const bookingRangeText = (b) => {
  if (!b) return '';
  const s = b.start_local || b.start_time;
  const e = b.end_local || b.end_time;
  const fs = fmtBookingTs(s);
  const fe = fmtBookingTs(e);
  if (!fs || !fe) return '';
  return `${fs} - ${fe}`;
};

/* ===================== URL type sync helpers ===================== */
const normalizeBookingType = (raw) => {
  const t = String(raw || '').toLowerCase().trim();
  if (!t) return null;
  if (['parking', 'car', 'cars'].includes(t)) return 'parking';
  if (['motorcycle', 'motor', 'bike', 'bikes'].includes(t)) return 'motorcycle';
  if (['desk', 'desks'].includes(t)) return 'desk';
  return null;
};

/* ===================== Commute options ===================== */
const COMMUTE_OPTIONS = [
  'Walk',
  'Bicycle',
  'E-Bike',
  'Motorcycle',
  'Motorcycle with a colleague',
  'Car',
  'Car with a colleague',
  'Public transport',
];
const COMMUTE_OPTIONS_PARKING = ['Car', 'Car with a colleague'];
const COMMUTE_OPTIONS_MOTOR = ['Motorcycle', 'Motorcycle with a colleague'];
const defaultCommuteForType = (t, saved = '') => {
  if (t === 'parking') return 'Car';
  if (t === 'motorcycle') return 'Motorcycle';
  if (t === 'desk') return saved || '';
  return '';
};
const commuteOptionsForType = (t, saved = '') => {
  if (t === 'parking') return COMMUTE_OPTIONS_PARKING;
  if (t === 'motorcycle') return COMMUTE_OPTIONS_MOTOR;
  if (t === 'desk') return saved ? [saved] : [];
  return COMMUTE_OPTIONS;
};

/* ===================== Estimated Distance options ===================== */
const DISTANCE_OPTIONS = ['0-5 km', '5-10 km', '10-20 km', '20 - 30 km', '30+ km'];

const isParkingOrMotor = (t) => t === 'parking' || t === 'motorcycle';

export default function Booking() {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('parking'); // 'parking' | 'motorcycle' | 'desk'
  const [resources, setResources] = useState({ desk: [], parking: [], motorcycle: [] });
  const [loading, setLoading] = useState(true);

  const [myBookings, setMyBookings] = useState([]);
  const [rangeBookings, setRangeBookings] = useState([]);

  const [modal, setModal] = useState({ open: false, res: null });

  // Notes removed; replaced by commute + estimated distance
  const [form, setForm] = useState({
    start_time: '',
    end_time: '',
    commute: '',
    estimated_distance: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const [deskFloor, setDeskFloor] = useState(DESK_FLOOR_ORDER[0] || 'Floor 2');

  const defaultStart = ceilMinute(new Date());
  const defaultEnd = addHours(defaultStart, 8);
  const [viewStart, setViewStart] = useState(defaultStart);
  const [viewEnd, setViewEnd] = useState(defaultEnd);

  /* === durasi dinamis agar End selalu mengikuti Start === */
  const [durationMs, setDurationMs] = useState(8 * 60 * 60 * 1000); // 8 jam

  /* === Preference (per-user): Commute + Distance === */
  const [savedCommute, setSavedCommute] = useState('');
  const [savedDistance, setSavedDistance] = useState('');
  const [prefLoading, setPrefLoading] = useState(true);

  // touched flags (avoid overriding when user already changed form)
  const [commuteTouched, setCommuteTouched] = useState(false);
  const [distanceTouched, setDistanceTouched] = useState(false);
  const commuteTouchedRef = useRef(false);
  const distanceTouchedRef = useRef(false);

  // avoid race updates when opening modal quickly
  const prefReqIdRef = useRef(0);

  const fetchMyPreference = useCallback(async () => {
    const reqId = ++prefReqIdRef.current;
    try {
      setPrefLoading(true);
      const r = await api.get('/my/commute');
      if (reqId !== prefReqIdRef.current) return { commute: '', distance: '' };

      const c = String(r.data?.commute || '').trim();
      const d = String(r.data?.estimated_distance || '').trim();

      setSavedCommute(c);
      setSavedDistance(d);
      return { commute: c, distance: d };
    } catch {
      if (reqId !== prefReqIdRef.current) return { commute: '', distance: '' };
      setSavedCommute('');
      setSavedDistance('');
      return { commute: '', distance: '' };
    } finally {
      if (reqId === prefReqIdRef.current) setPrefLoading(false);
    }
  }, []);

  const tryUpdatePreference = useCallback(async ({ commute, distance }) => {
    // Try update preference. If backend doesn't support it (404) or rejects (400), throw and caller will fallback.
    const c = String(commute || '').trim();
    const d = String(distance || '').trim();

    if (!c || !d) throw new Error('invalid');

    await api.put('/my/commute', { commute: c, estimated_distance: d });

    // If ok -> update local saved state
    setSavedCommute(c);
    setSavedDistance(d);
    return { commute: c, distance: d };
  }, []);

  const onChangeViewStart = (val) => {
    const s = fromInputLocal(val);
    if (isNaN(s)) return;
    setViewStart(s);
    setViewEnd(new Date(s.getTime() + durationMs));
  };
  const onChangeViewEnd = (val) => {
    const e = fromInputLocal(val);
    if (isNaN(e)) return;
    setViewEnd(e);
    const ms = Math.max(60 * 1000, e - viewStart); // minimal 1 menit
    setDurationMs(ms);
  };

  /* ===================== SYNC: URL -> activeTab ===================== */
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const wanted =
      normalizeBookingType(sp.get('type')) ||
      normalizeBookingType(sp.get('tab')) ||
      normalizeBookingType(sp.get('resource'));
    if (wanted && wanted !== activeTab) setActiveTab(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  /* ===================== SYNC: activeTab -> URL (replace) ===================== */
  const setTab = (tab) => {
    const next = normalizeBookingType(tab) || 'parking';
    setActiveTab(next);

    const sp = new URLSearchParams(location.search);
    sp.set('type', next);
    navigate(`${location.pathname}?${sp.toString()}`, { replace: true });
  };

  const refreshResources = async () => {
    const [deskRes, parkRes, motorRes] = await Promise.all([
      api.get('/resources', { params: { type: 'Desk' } }),
      api.get('/resources', { params: { type: 'Parking' } }),
      api.get('/resources', { params: { type: 'Motorcycle' } }),
    ]);
    setResources({
      desk: Array.isArray(deskRes.data) ? deskRes.data : [],
      parking: Array.isArray(parkRes.data) ? parkRes.data : [],
      motorcycle: Array.isArray(motorRes.data) ? motorRes.data : [],
    });
  };

  const refreshMine = async () => {
    const myRes = await api.get('/bookings', { params: { mine: 1 } });
    const now = new Date();
    const cleaned = (Array.isArray(myRes.data) ? myRes.data : []).filter((b) =>
      isActiveBooking(b, now)
    );
    setMyBookings(cleaned);
  };

  const refreshRangeBookings = async (vs, ve) => {
    const params = { start: toSqlLocal(vs), end: toSqlLocal(ve) };
    const res = await api.get('/bookings', { params });
    const list = Array.isArray(res.data) ? res.data : [];
    const now = new Date();
    const cleaned = list.filter((b) => {
      if (!isActiveBooking(b, now)) return false;
      const { start, end } = normalizeTimes(b);
      return overlaps(vs, ve, start, end);
    });
    setRangeBookings(cleaned);
  };

  const refreshAll = async () => {
    try {
      setLoading(true);
      await Promise.all([
        refreshResources(),
        refreshMine(),
        refreshRangeBookings(viewStart, viewEnd),
        fetchMyPreference(),
      ]);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    if (!(viewStart instanceof Date) || !(viewEnd instanceof Date)) return;
    if (isNaN(viewStart) || isNaN(viewEnd)) return;
    if (viewEnd <= viewStart) return;
    refreshRangeBookings(viewStart, viewEnd).catch(() => {});
  }, [viewStart, viewEnd]);

  /* ====== desks per lantai ====== */
  const desksByFloorName = useMemo(() => {
    const map = {};
    for (const r of resources.desk) {
      const key = r.floor_name || 'Floor 1';
      (map[key] ||= []).push(r);
    }
    return map;
  }, [resources.desk]);

  const floorNames = useMemo(
    () =>
      (DESK_FLOOR_ORDER.length ? DESK_FLOOR_ORDER : Object.keys(desksByFloorName).sort()),
    [desksByFloorName]
  );

  const takenDeskIds = useMemo(() => {
    const set = new Set();
    for (const b of rangeBookings) {
      const type = (b.type_name || b.type || '').toLowerCase();
      if (type !== 'desk') continue;
      set.add(b.resource_id);
    }
    return set;
  }, [rangeBookings]);

  const deskBookingByResourceId = useMemo(() => {
    const map = new Map();
    for (const b of rangeBookings) {
      const type = (b.type_name || b.type || '').toLowerCase();
      if (type !== 'desk') continue;
      map.set(b.resource_id, b);
    }
    return map;
  }, [rangeBookings]);

  const takenParkingIds = useMemo(() => {
    const set = new Set();
    for (const b of rangeBookings) {
      const type = (b.type_name || b.type || '').toLowerCase();
      if (type !== 'parking') continue;
      set.add(b.resource_id);
    }
    return set;
  }, [rangeBookings]);

  const parkingBookingByResourceId = useMemo(() => {
    const map = new Map();
    for (const b of rangeBookings) {
      const type = (b.type_name || b.type || '').toLowerCase();
      if (type !== 'parking') continue;
      map.set(b.resource_id, b);
    }
    return map;
  }, [rangeBookings]);

  const takenMotorcycleIds = useMemo(() => {
    const set = new Set();
    for (const b of rangeBookings) {
      const type = (b.type_name || b.type || '').toLowerCase();
      if (type !== 'motorcycle') continue;
      set.add(b.resource_id);
    }
    return set;
  }, [rangeBookings]);

  const motorcycleBookingByResourceId = useMemo(() => {
    const map = new Map();
    for (const b of rangeBookings) {
      const type = (b.type_name || b.type || '').toLowerCase();
      if (type !== 'motorcycle') continue;
      map.set(b.resource_id, b);
    }
    return map;
  }, [rangeBookings]);

  /* ====== open modal ====== */
  const openFor = async (res) => {
    const s = viewStart;
    const t = (res?.type_name || res?.type || '').toLowerCase();
    const e =
      t === 'parking' || t === 'motorcycle'
        ? null
        : (viewEnd > viewStart ? viewEnd : addHours(viewStart, 2));

    // reset touched flags for new modal open
    setCommuteTouched(false);
    setDistanceTouched(false);
    commuteTouchedRef.current = false;
    distanceTouchedRef.current = false;

    // open modal first (snappy)
    setModal({ open: true, res });

    // set initial form using local saved preference (fast auto-fill)
    setForm({
      start_time: t === 'motorcycle' ? toDateInputValue(s) : toInputValue(s),
      end_time: e ? toInputValue(e) : '',
      commute: defaultCommuteForType(t, savedCommute),
      estimated_distance: savedDistance || '',
    });

    // ensure latest preference fetched so auto-fill works across any resource
    // only overwrite if user hasn't touched and field still empty
    const latest = await fetchMyPreference();
    setForm((p) => {
      const next = { ...p };
      if (!commuteTouchedRef.current && !next.commute && t === 'desk')
        next.commute = latest.commute || '';
      if (!distanceTouchedRef.current && !next.estimated_distance)
        next.estimated_distance = latest.distance || '';
      return next;
    });
  };

  /* ====== submit ====== */
  const book = async (ev) => {
    ev.preventDefault();
    if (!modal.res) return;

    const now = new Date();
    const type = (modal.res.type_name || modal.res.type || '').toLowerCase();
    const isMotorcycleType = type === 'motorcycle';
    const start = isMotorcycleType ? fromDateInputLocal(form.start_time) : fromInputLocal(form.start_time);
    const end =
      type === 'parking'
        ? start
        : isMotorcycleType
          ? addDays(start, 1)
          : fromInputLocal(form.end_time);

    if (isNaN(start) || isNaN(end)) {
      return toast.error(isMotorcycleType ? 'Invalid booking date' : 'Invalid date/time');
    }
    if (type === 'desk' && end <= start) return toast.error('End time must be after start time');
    if (isMotorcycleType) {
      if (end <= now) return toast.error('Booking date cannot be in the past');
    } else if (start < now) {
      return toast.error('Start time cannot be in the past');
    }

    // commute + distance required
    const pickedCommute = String(form.commute || defaultCommuteForType(type, savedCommute)).trim();
    const pickedDistance = String(form.estimated_distance || '').trim();
    if (!pickedCommute) return toast.error('Please select your commute preference.');
    if (!pickedDistance) return toast.error('Please select your estimated distance.');

    // match backend rule:
    // - desk blocks desk overlaps
    // - parking/motorcycle blocks both parking+motorcycle overlaps for this user
    const conflictTypes = isParkingOrMotor(type)
      ? new Set(['parking', 'motorcycle'])
      : new Set([type]);

    const selfConflict = (myBookings || []).some((b) => {
      const t = (b.type_name || b.type || '').toLowerCase();
      if (!conflictTypes.has(t)) return false;
      const { start: bS, end: bE } = normalizeTimes(b);
      return overlaps(start, end, bS, bE);
    });

    if (selfConflict) {
      if (isParkingOrMotor(type)) {
        return toast.error('Time overlaps with your existing car or motorcycle booking.');
      }
      const label = type === 'desk' ? 'Desk' : 'Resource';
      return toast.error(`Time overlaps with your existing ${label} booking.`);
    }

    // Verify slot availability against server list (keeps your old behavior)
    try {
      const params = { start: toSqlLocal(start), end: toSqlLocal(end) };
      const res = await api.get('/bookings', { params });
      const list = Array.isArray(res.data) ? res.data : [];
      const activeOverlaps = list.filter((b) => {
        if (!isActiveBooking(b, now)) return false;
        const { start: bS, end: bE } = normalizeTimes(b);
        return overlaps(start, end, bS, bE);
      });
      const resourceClash = activeOverlaps.some(
        (b) => Number(b.resource_id) === Number(modal.res.id)
      );
      if (resourceClash) {
        return toast.error('Selected resource is not available for the chosen time window.');
      }
    } catch (err) {
      console.error(err);
      return toast.error('Failed to verify availability. Please try again.');
    }

    setSubmitting(true);
    try {
      // ===== Preference logic (commute + distance) =====
      // If saved preference exists and user picked different, try update preference first.
      // If update not supported / rejected, fallback to saved preference so booking still succeeds.
      let commuteToSend = pickedCommute;
      let distanceToSend = pickedDistance;

      const hasSavedPref = !!savedCommute || !!savedDistance;
      const changedPref =
        (savedCommute && pickedCommute !== savedCommute) ||
        (savedDistance && pickedDistance !== savedDistance);

      if (hasSavedPref && changedPref) {
        try {
          await tryUpdatePreference({ commute: pickedCommute, distance: pickedDistance });
          commuteToSend = pickedCommute;
          distanceToSend = pickedDistance;
        } catch (_e) {
          // fallback to saved pref (only if exists)
          if (savedCommute) {
            commuteToSend = savedCommute;
            setForm((p) => ({ ...p, commute: savedCommute }));
          }
          if (savedDistance) {
            distanceToSend = savedDistance;
            setForm((p) => ({ ...p, estimated_distance: savedDistance }));
          }

          const fallbackCommute = savedCommute || pickedCommute;
          const fallbackDistance = savedDistance || pickedDistance;

          toast(
            () => (
              <span>
                Your preference is already saved. Using:
                <br />
                <b>{fallbackCommute}</b>  <b>{fallbackDistance}</b>
                <br />
                (Change it in <b>My Bookings</b> if needed.)
              </span>
            ),
            { id: 'pref-fallback' }
          );

          commuteToSend = fallbackCommute;
          distanceToSend = fallbackDistance;
        }
      }

      // NEW: try send estimated_distance; if backend doesn't support, retry without it (safe)
      const basePayload = {
        resource_id: modal.res.id,
        start_time: toSqlLocal(start),
        ...(modalType === 'desk' ? { end_time: toSqlLocal(end) } : {}),
        commute: commuteToSend,
      };

      try {
        await api.post('/bookings', { ...basePayload, estimated_distance: distanceToSend });
      } catch (e1) {
        const msg1 = String(e1?.response?.data?.error || e1?.message || '');
        const status1 = e1?.response?.status;

        // Only fallback on likely "unknown field" / validation mismatch cases.
        // If it's a real booking error (conflict/etc), keep original error.
        const looksLikeUnknownField =
          status1 === 400 &&
          /(unknown|unexpected|not allowed|additional properties|invalid field|estimated_distance|distance)/i.test(
            msg1
          );

        if (!looksLikeUnknownField) throw e1;

        // Retry WITHOUT estimated_distance so booking never breaks
        await api.post('/bookings', basePayload);

        toast(() => (
          <span>
            Booking created, but <b>Estimated Distance</b> isn&apos;t supported by your server yet.
            <br />
            (Frontend captured it; backend update needed to store.)
          </span>
        ));
      }

      // Update local saved preference immediately so next modal auto-fills (even before refetch)
      if (!savedCommute || savedCommute !== commuteToSend) setSavedCommute(commuteToSend);
      if (!savedDistance || savedDistance !== distanceToSend) setSavedDistance(distanceToSend);

      toast.success('Booking created!');
      setModal({ open: false, res: null });
      setForm({ start_time: '', end_time: '', commute: '', estimated_distance: '' });

      setCommuteTouched(false);
      setDistanceTouched(false);
      commuteTouchedRef.current = false;
      distanceTouchedRef.current = false;

      await Promise.all([refreshMine(), refreshRangeBookings(viewStart, viewEnd)]);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to book';

      // One more safety: if server rejects because preference already set, retry once with saved values (if any)
      const looksLikeAlreadyFilled = String(msg).toLowerCase().includes('already filled');

      if (looksLikeAlreadyFilled && (savedCommute || savedDistance)) {
        try {
          const payload2 = {
            resource_id: modal.res.id,
            start_time: toSqlLocal(start),
            ...(modalType === 'desk' ? { end_time: toSqlLocal(end) } : {}),
            commute: savedCommute || pickedCommute,
          };

          // try with distance first (safe), fallback without if needed
          try {
            await api.post('/bookings', {
              ...payload2,
              estimated_distance: savedDistance || pickedDistance,
            });
          } catch (e2a) {
            const msg2 = String(e2a?.response?.data?.error || e2a?.message || '');
            const status2 = e2a?.response?.status;

            const looksLikeUnknownField2 =
              status2 === 400 &&
              /(unknown|unexpected|not allowed|additional properties|invalid field|estimated_distance|distance)/i.test(
                msg2
              );

            if (!looksLikeUnknownField2) throw e2a;
            await api.post('/bookings', payload2);
          }

          toast.success('Booking created!');
          setModal({ open: false, res: null });
          setForm({ start_time: '', end_time: '', commute: '', estimated_distance: '' });

          setCommuteTouched(false);
          setDistanceTouched(false);
          commuteTouchedRef.current = false;
          distanceTouchedRef.current = false;

          await Promise.all([refreshMine(), refreshRangeBookings(viewStart, viewEnd)]);
          return;
        } catch (e2) {
          toast.error(e2.response?.data?.error || 'Failed to book');
          return;
        }
      }

      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  /* ====== UI helper ====== */
  const onClickNowPlus2h = () => {
    const s = ceilMinute(new Date());
    const e = addHours(s, 8);
    setViewStart(s);
    setViewEnd(e);
    setDurationMs(8 * 60 * 60 * 1000);
  };

  const refreshAvailability = () => {
    if (viewEnd <= viewStart) {
      toast.error('View End must be after View Start');
      return;
    }
    refreshRangeBookings(viewStart, viewEnd).catch(() => {});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  /* ====== resources with taken flag merged by range ====== */
  const deskResourcesForFloor = (name) => {
    const list = desksByFloorName[name] || [];
    return list.map((r) => {
      const booking = deskBookingByResourceId.get(r.id);
      return {
        ...r,
        is_booked: takenDeskIds.has(r.id),
        booked_by: booking?.username || booking?.user_name || booking?.user || '',
        booked_range: bookingRangeText(booking),
      };
    });
  };

  const parkingWithTaken = resources.parking.map((r) => {
    const booking = parkingBookingByResourceId.get(r.id);
    return {
      ...r,
      is_booked: takenParkingIds.has(r.id),
      booked_by: booking?.username || booking?.user_name || booking?.user || '',
      booked_range: bookingRangeText(booking),
    };
  });

  const motorcycleWithTaken = resources.motorcycle.map((r) => {
    const booking = motorcycleBookingByResourceId.get(r.id);
    return {
      ...r,
      is_booked: takenMotorcycleIds.has(r.id),
      booked_by: booking?.username || booking?.user_name || booking?.user || '',
      booked_range: bookingRangeText(booking),
    };
  });

  const modalType = (modal.res?.type_name || modal.res?.type || '').toLowerCase();

  return (
    <div className="space-y-6">
      {/* View range controls */}
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto] items-end gap-3 sm:flex sm:flex-wrap">
        <div className="min-w-0 sm:w-auto">
          <label className="block text-sm text-gray-300 mb-1">View Start</label>
          <input
            type="datetime-local"
            className="w-full sm:w-[240px] bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-100"
            value={toInputValue(viewStart)}
            onChange={(e) => onChangeViewStart(e.target.value)}
          />
        </div>
        <div className="min-w-0 sm:w-auto">
          <label className="block text-sm text-gray-300 mb-1">View End</label>
          <input
            type="datetime-local"
            className="w-full sm:w-[240px] bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-100"
            value={toInputValue(viewEnd)}
            onChange={(e) => onChangeViewEnd(e.target.value)}
            min={toInputValue(viewStart)}
          />
        </div>

        <div className="w-auto">
          <Button
            variant="secondary"
            onClick={onClickNowPlus2h}
            className="whitespace-nowrap text-xs sm:text-sm px-2 py-1 sm:px-3 sm:py-2"
          >
            Now +8h
          </Button>
        </div>
        <div className="w-auto">
          <Button
            onClick={refreshAvailability}
            className="flex items-center gap-2 whitespace-nowrap text-xs sm:text-sm px-2 py-1 sm:px-3 sm:py-2"
          >
            <RefreshCcw className="w-4 h-4" />
            <span className="hidden sm:inline">Check</span>
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setTab('parking')}
          className={`px-4 py-3 rounded-xl font-medium ${
            activeTab === 'parking'
              ? 'bg-amber-600 text-white'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          <Car className="inline w-4 h-4 mr-2" /> Car Slots
        </button>

        <button
          onClick={() => setTab('motorcycle')}
          className={`px-4 py-3 rounded-xl font-medium ${
            activeTab === 'motorcycle'
              ? 'bg-sky-600 text-white'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          <Bike className="inline w-4 h-4 mr-2" /> Motorcycle Slots
        </button>

        <button
          onClick={() => setTab('desk')}
          className={`px-4 py-3 rounded-xl font-medium ${
            activeTab === 'desk'
              ? 'bg-emerald-600 text-white'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          <Monitor className="inline w-4 h-4 mr-2" /> Desk Spaces
        </button>
      </div>

      {/* Content */}
      {activeTab === 'parking' ? (
        <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {parkingWithTaken.map((r) => (
            <Card
              key={r.id}
              className={`border p-4 sm:p-6 ${
                r.is_booked ? 'border-red-500 bg-red-900/20' : 'border-gray-700'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 sm:p-2 bg-amber-600 rounded-lg">
                    <Car className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm sm:text-base">{r.name}</div>
                    <div className="text-gray-400 text-xs sm:text-sm">
                      {r.building_name || ''}
                      {r.floor_name ? ' - ' + r.floor_name : ''}
                    </div>
                    {r.is_booked && r.booked_by ? (
                      <div className="text-gray-400 text-xs sm:text-sm">Booked by {r.booked_by}</div>
                    ) : null}
                    {r.is_booked && r.booked_range ? (
                      <div className="text-gray-400 text-[10px] sm:text-xs">Time: {r.booked_range}</div>
                    ) : null}
                  </div>
                </div>
                <span
                  className={`px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                    r.is_booked
                      ? 'bg-red-200 text-red-800'
                      : 'bg-emerald-200 text-emerald-800'
                  }`}
                >
                  {r.is_booked ? 'Booked' : 'Available'}
                </span>
              </div>
              <div className="mt-2 sm:mt-3 flex justify-end">
                <Button disabled={!!r.is_booked} onClick={() => openFor(r)} className="px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm">
                  Book
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : activeTab === 'motorcycle' ? (
        <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {motorcycleWithTaken.map((r) => (
            <Card
              key={r.id}
              className={`border p-4 sm:p-6 ${
                r.is_booked ? 'border-red-500 bg-red-900/20' : 'border-gray-700'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 sm:p-2 bg-sky-600 rounded-lg">
                    <Bike className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm sm:text-base">{r.name}</div>
                    <div className="text-gray-400 text-xs sm:text-sm">
                      {r.building_name || ''}
                      {r.floor_name ? ' - ' + r.floor_name : ''}
                    </div>
                    {r.is_booked && r.booked_by ? (
                      <div className="text-gray-400 text-xs sm:text-sm">Booked by {r.booked_by}</div>
                    ) : null}
                    {r.is_booked && r.booked_range ? (
                      <div className="text-gray-400 text-[10px] sm:text-xs">Time: {r.booked_range}</div>
                    ) : null}
                  </div>
                </div>
                <span
                  className={`px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                    r.is_booked
                      ? 'bg-red-200 text-red-800'
                      : 'bg-emerald-200 text-emerald-800'
                  }`}
                >
                  {r.is_booked ? 'Booked' : 'Available'}
                </span>
              </div>
              <div className="mt-2 sm:mt-3 flex justify-end">
                <Button disabled={!!r.is_booked} onClick={() => openFor(r)} className="px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm">
                  Book
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <DeskFloorPicker current={deskFloor} setCurrent={setDeskFloor} floors={floorNames} />
          <div className="w-full">
            <DeskFloorMap
              floorName={deskFloor}
              desks={deskResourcesForFloor(deskFloor)}
              map={DESK_MAPS[deskFloor]}
              onPick={(r) => openFor(r)}
            />
          </div>
        </div>
      )}
      {/* Modal */}
      <Modal
        isOpen={modal.open}
        onClose={() => {
          setModal({ open: false, res: null });

          setCommuteTouched(false);
          setDistanceTouched(false);
          commuteTouchedRef.current = false;
          distanceTouchedRef.current = false;
        }}
        title="Create Booking"
        size="md"
      >
        {modal.res && (
          <form onSubmit={book} className="space-y-4">
            <div className="bg-gray-700 rounded-lg p-4 mb-4">
              <div className="flex items-center space-x-2 mb-2">
                {modalType === 'parking' ? (
                  <Car className="h-5 w-5 text-amber-400" />
                ) : modalType === 'motorcycle' ? (
                  <Bike className="h-5 w-5 text-sky-400" />
                ) : (
                  <Monitor className="h-5 w-5 text-emerald-400" />
                )}
                <span className="font-semibold text-white">{modal.res.name}</span>
              </div>
              <p className="text-gray-400 text-sm">
                {modal.res.building_name || ''}
                {modal.res.floor_name ? `  ${modal.res.floor_name}` : ''}
              </p>
            </div>

            {modalType === 'motorcycle' ? (
              <Input
                label="Booking Day"
                type="date"
                value={form.start_time}
                onChange={(e) => setForm((p) => ({ ...p, start_time: e.target.value }))}
                required
                min={toDateInputValue(new Date())}
              />
            ) : (
              <Input
                label="Start Time"
                type="datetime-local"
                value={form.start_time}
                onChange={(e) => setForm((p) => ({ ...p, start_time: e.target.value }))}
                required
              />
            )}
            {modalType === 'desk' && (
              <Input
                label="End Time"
                type="datetime-local"
                value={form.end_time}
                onChange={(e) => setForm((p) => ({ ...p, end_time: e.target.value }))}
                required
                min={form.start_time}
              />
            )}

            {/* âœ… Commute dropdown */}
            <div>
              <label className="block text-sm text-gray-300 mb-1">How Do You Commute ?</label>
              <select
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-100"
                value={form.commute || defaultCommuteForType(modalType, savedCommute)}
                onChange={(e) => {
                  setCommuteTouched(true);
                  commuteTouchedRef.current = true;
                  setForm((p) => ({ ...p, commute: e.target.value }));
                }}
                disabled={prefLoading || modalType === 'desk'}
                required
              >
                {commuteOptionsForType(modalType, savedCommute).length === 0 ? (
                  <option value="">{prefLoading ? 'Loading...' : 'Select...'}</option>
                ) : null}
                {commuteOptionsForType(modalType, savedCommute).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>

              <div className="mt-1 text-xs text-gray-400">
                {savedCommute ? (
                  <>
                    Saved preference: <span className="text-gray-200">{savedCommute}</span>.
                  </>
                ) : (
                  <>This preference will be saved to your profile (and auto-filled next time).</>
                )}
              </div>
            </div>

            {/* âœ… Estimated Distance dropdown */}
            <div>
              <label className="block text-sm text-gray-300 mb-1">Estimated Distance</label>
              <select
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-100"
                value={form.estimated_distance}
                onChange={(e) => {
                  setDistanceTouched(true);
                  distanceTouchedRef.current = true;
                  setForm((p) => ({ ...p, estimated_distance: e.target.value }));
                }}
                disabled={prefLoading}
                required
              >
                <option value="">{prefLoading ? 'Loading...' : 'Select...'}</option>
                {DISTANCE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>

              <div className="mt-1 text-xs text-gray-400">
                {savedDistance ? (
                  <>
                    Saved preference: <span className="text-gray-200">{savedDistance}</span>.
                  </>
                ) : (
                  <>This preference will be saved to your profile (and auto-filled next time).</>
                )}
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setModal({ open: false, res: null });

                  setCommuteTouched(false);
                  setDistanceTouched(false);
                  commuteTouchedRef.current = false;
                  distanceTouchedRef.current = false;
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" loading={submitting} className="flex-1">
                Book Resource
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}













































